/**
 * Whether the native apps may offer the account-less trial. Pure so it is unit
 * testable; the React hook (hooks/useNativeTrial.ts) feeds it the live values.
 *
 * - `enabled`/`native_enabled` come from GET /api/anon/status (server flags;
 *   `native_enabled` stays off until both stores' privacy declarations list
 *   the install identifier — see docs/APP-REVIEW-NOTES.md).
 * - `childDeclared`: an Android install whose neutral age screen was answered
 *   with under 13 never sees the trial (Play Families — same rule as Google
 *   Sign-In). iOS has no age screen; the trial shows there like on the web.
 */
export interface AnonStatus {
  enabled: boolean;
  page_count: number;
  native_enabled?: boolean;
  /** Free trial stories left in the rolling 24 h window (landing-page scarcity line; absent while the trial is off). */
  remaining_today?: number;
  /** Trial v3 (2026-09-20): ask for the child's name after the one tap on the ad landings (false = old one-tap). */
  name_sheet?: boolean;
  /** Closing slide A′ (2026-09-29): pool targets with a finished story right now, with the language and age
   *  their key was built for (absent while the pool is off or the count failed → never "sofort da"). */
  pool_ready?: Record<string, { language: string; child_age: number }>;
  /** Folge 3 on claim (2026-10-02, backend/folge3.py): a claimed trial's next episode is queued as a gift. */
  folge3_on_claim?: boolean;
  /** Folge 3 teaser (2026-10-15, lib/trialTeaser.ts): the own trial's closing slide shows its next episode, made
   *  before the claim (server flag FOLGE3_TEASER; absent from older servers → off). */
  folge3_teaser?: boolean;
  /** „Erinnere mich morgen Abend“ (2026-10-02): the e-mail reminder field may render (server flag
   *  ANON_REMINDER_EMAIL_ENABLED; absent from older servers → off). */
  reminder_email?: boolean;
}

export function nativeTrialAvailable(
  status: AnonStatus | null | undefined,
  ctx: { isNative: boolean; childDeclared: boolean; override?: boolean },
): boolean {
  if (ctx.childDeclared) return false;
  // Tester override (Settings card for is_free accounts / a localStorage key on
  // the web): lets Kai exercise the app's trial path before the server flag
  // is switched on for everyone. Only the master flag still has to be on.
  if (ctx.override) return !!status?.enabled;
  if (!ctx.isNative) return false;
  return !!status?.enabled && !!status?.native_enabled;
}
